'use client';

import dynamic from 'next/dynamic';
import { useRouter, useSearchParams } from 'next/navigation';
import { useState } from 'react';

import { NavTabs, SearchField } from '@/components/molecules';
import { NoDataMessage } from '@/components/molecules/NoDataMessage';
import { TeamInfoData, TeamLeaguesErrorPage } from '@/components/organisms';
import { TeamInfoTableSkeleton } from '@/components/organisms/skeletons/TeamInfoTableSkeleton';
import { DivisionInfoData as DivisionInfoDataType } from '@/entities/DivisionInfoData';
import { PermissionRole } from '@/entities/PermissionRoles';
import { ActivityTeamInfoData, TeamInfoData as TeamInfoDataType } from '@/entities/Team';
import { useActiveTeams, usePlayerActivity, useSeasonDivisions } from '@/hooks/hooks';
import { useSeasonDivisionPool } from '@/hooks/leagueSeasonDivision/useSeasonDivisionPool';
import { useTeamLeagueSeasonDivision } from '@/hooks/leagueSeasonDivision/useTeamLeagueSeasonDivision';
import { useCurrenciesQuery } from '@/hooks/lookups/useCurrencyQuery';
import { useTeamLeagueDetailsQuery } from '@/hooks/team-leagues/useDetailsQuery';
import { useOrganizationStore } from '@/store/organization-store';
import { usePermissionStore } from '@/store/permission-store';
import { Options } from '@/utils/types';

const Select = dynamic(() => import('@pickleballinc/react-ui').then((mod) => mod.Select), {
	ssr: false
});

interface DivisionDataProps {
	divisionsInfoData?: DivisionInfoDataType[];
	teamLeagueId: string;
	seasonsOptions?: Options[];
	type: 'active-teams' | 'player-activity';
}

export const DivisionInfoData = ({ seasonsOptions = [], teamLeagueId, type }: DivisionDataProps) => {
	const permissionStore = usePermissionStore();
	const shouldHideData =
		permissionStore.userPermission === PermissionRole.CAN_MANAGE_GAMEDAYS || permissionStore.userPermission === PermissionRole.COCAPTAIN;

	const searchParams = useSearchParams();
	const router = useRouter();

	const selectedSeason = searchParams.get('season') || seasonsOptions[0]?.value;

	const [searchTerm, setSearchTerm] = useState('');

	const store = useOrganizationStore();
	const { data: divisionsInfoData } = useSeasonDivisions(store.organizationUuid, store.teamLeagueUuid, selectedSeason || '');

	const selectedDivision = searchParams.get('division') || (divisionsInfoData?.length === 1 ? divisionsInfoData[0]?.uuid : '');

	// We fetch this API only to get the season division uuid and put it in the pool data api call
	const { data: seasonDivision } = useTeamLeagueSeasonDivision({
		teamLeagueUuid: teamLeagueId,
		seasonUuid: selectedSeason || '',
		divisionUuid: selectedDivision || ''
	});

	const { data: poolData, isLoading: isLoadingPoolData } = useSeasonDivisionPool(seasonDivision?.data?.uuid || '');
	const poolId = poolData?.data ? poolData.data : '';

	const {
		data: playerActivityData,
		isLoading: playerActivityLoading,
		isFetched: playerActivityFetched
	} = usePlayerActivity(store.organizationUuid, teamLeagueId, selectedSeason || '', type === 'active-teams');

	const {
		data: activeTeamsData,
		isLoading: activeTeamsLoading,
		isFetched: activeTeamsFetched
	} = useActiveTeams(store.organizationUuid, teamLeagueId, selectedSeason || '', type === 'player-activity');

	const { data: leagueInfoResponse } = useTeamLeagueDetailsQuery(teamLeagueId);
	const { data: currencies } = useCurrenciesQuery(true);

	const data = type === 'player-activity' ? playerActivityData : activeTeamsData;
	const isLoading = type === 'player-activity' ? playerActivityLoading : activeTeamsLoading;
	const isFetched = type === 'player-activity' ? playerActivityFetched : activeTeamsFetched;

	const options = [
		{
			label: 'All',
			value: 'all'
		},
		...(divisionsInfoData || []).map((division) => ({
			label: division.title,
			value: division.uuid
		}))
	];

	const handleSeasonChange = (selectedOption: string) => {
		const params = new URLSearchParams();

		if (selectedOption) {
			params.set('season', selectedOption);
		}

		params.delete('division');
		router.replace(`?${params.toString()}`, { scroll: false });
	};

	const handleDivisionChange = (selectedOption: string) => {
		router.replace(`?season=${selectedSeason}&division=${selectedOption}`, { scroll: false });
	};

	const activeTeams = () => {
		if (selectedDivision === '') {
			return (data || []).flatMap((division: any) => division.teams);
		} else {
			const division = (data || []).find((div) => div.uuid === selectedDivision);
			return division ? division.teams : [];
		}
	};

	if (!seasonsOptions.length && !isLoading) {
		return <NoDataMessage title="No rally here!" description="Looks like no season is currently in play. Hang tight and check back soon!" />;
	}

	return (
		<div>
			<div className="mb-4 flex w-full flex-col justify-between space-y-4 lg:mb-0 lg:flex-row lg:items-end lg:space-y-2">
				<Select
					selectTheme="orange"
					aria-activedescendant={undefined}
					id="season-select"
					className="max-w-[465px] lg:w-[300px]"
					value={seasonsOptions.find(({ value }) => value === selectedSeason) || seasonsOptions[0]}
					onChange={(e: any) => handleSeasonChange(e.value)}
					options={seasonsOptions}
					styles={{
						singleValue: (base) => ({
							...base,
							fontWeight: 'bold',
							fontSize: '22px',
							color: '#000000',
							backgroundColor: 'transparent'
						}),
						control: (base) => ({
							...base,
							border: 'none',
							boxShadow: 'none',
							backgroundColor: 'transparent',
							cursor: 'pointer',
							'&:hover': {
								border: 'none'
							}
						}),
						indicatorSeparator: () => ({
							display: 'none'
						}),
						menu: (base) => ({
							...base,
							border: '1px solid #D1D5DB',
							backgroundColor: '#ffffff',
							boxShadow: 'none',
							zIndex: 100
						}),
						menuList: (base) => ({
							...base,
							fontSize: 14
						}),
						option: (base, state) => ({
							...base,
							backgroundColor: state.isSelected ? '#FDEAD7' : state.isFocused ? '#FCD3B0' : 'transparent',
							color: state.isSelected ? '#000000' : '#000000',
							'&:active': {
								backgroundColor: state.isSelected ? '#FDEAD7' : '#FCD3B0'
							}
						})
					}}
				/>
				<SearchField searchTerm={searchTerm} setSearchTerm={setSearchTerm} placeholder="Search team, player" />
			</div>

			{divisionsInfoData && divisionsInfoData?.length > 1 ? (
				<div className="my-2 flex flex-col">
					<label htmlFor="division-select" className="mb-1 text-sm">
						Division
					</label>
					<Select
						selectTheme="orange"
						aria-activedescendant={undefined}
						id="division-select"
						className="max-w-[465px] lg:w-[300px]"
						labelClassName="block"
						value={options.find(({ value }) => value === selectedDivision) || options[0]}
						onChange={(e: any) => handleDivisionChange(e.value)}
						options={options}
					/>
				</div>
			) : null}
			<NavTabs
				teamLeagueId={teamLeagueId}
				seasonId={selectedSeason}
				divisionId={selectedDivision}
				divisionCount={divisionsInfoData ? divisionsInfoData.length : 0}
				poolId={poolId}
				isLoadingPoolData={isLoadingPoolData}
			/>

			{!isLoading && (!divisionsInfoData || divisionsInfoData.length === 0) ? (
				<NoDataMessage title="No rally here!" description="There's no data to show yet. Stretch it out and check back later!" />
			) : shouldHideData ? (
				<NoDataMessage title="Not Authorized" description={`You don't have permission to view this data.`} />
			) : (
				<div className="flex flex-col gap-4">
					{!isLoading && activeTeams().length > 0 ? (
						activeTeams().map((team: ActivityTeamInfoData | TeamInfoDataType) => (
							<TeamInfoData
								key={team.uuid}
								teamLeagueId={teamLeagueId}
								teamId={team.uuid}
								players={team.players}
								name={team.name}
								seasonId={selectedSeason || ''}
								isFreeAgents={team.allowFreeAgents}
								leagueInfo={leagueInfoResponse?.data}
								currencies={currencies}
							/>
						))
					) : isLoading ? (
						<TeamInfoTableSkeleton />
					) : isFetched && activeTeams()?.length === 0 ? (
						<NoDataMessage
							title={searchParams ? 'Nothing in the Kitchen!' : 'No rally here!'}
							description={
								searchParams
									? "Sorry, we didn't find any results, so please try re-serving that search with new keywords."
									: "There's no data to show yet. Stretch it out and check back later!"
							}
						/>
					) : (
						isFetched && (
							<TeamLeaguesErrorPage
								title="Unforced error!"
								description=" We just hit out of bounds...Please try back later."
								shouldTakeScreenWidth={false}
							/>
						)
					)}
				</div>
			)}
		</div>
	);
};
